<template>
  <div class="layout olap-reports">
    <m-category ref="categoryModal" :code="code" @created="onCategoryAdded" @updated="onCategoryUpdated" />
    <m-report
      ref="reportModal"
      :code="code"
      @created="onReportAdded"
      @updated="onReportUpdated"
      @removed="onReportRemoved"
    />
    <m-confirm ref="confirm" mode="danger" />
    <m-description :code="code" ref="description" />

    <div class="layout-header">
      <h1 class="layout-title">{{ title }}</h1>

      <div class="layout-header-actions">
        <!-- <div class="layout-header-action">
          <c-button
            size="small"
            :icon="{
              icon: 'bookmark',
              activeColor: 'white',
            }"
            :tooltip="{
              text: 'Добавить в закладки',
              activeText: 'Убрать из закладок',
            }"
            active-color="blue"
            :active="bookmark"
            @click="bookmark = !bookmark"
          />
        </div> -->

        <div class="layout-header-action">
          <c-button
            size="small"
            label="Новая категория"
            :icon="{
              icon: 'plus',
              color: 'primary',
            }"
            @click="createCategory"
          />
        </div>

        <div class="layout-header-action">
          <c-button
            size="small"
            label="Новый отчет"
            :icon="{
              icon: 'plus',
              color: 'primary',
            }"
            @click="createReport"
          />
        </div>
      </div>
    </div>

    <div class="olap-reports__content">
      <loader v-if="loading" :active="loading" :height="450" label="Данные загружаются" />

      <div v-else>
        <div class="olap-reports__search">
          <c-input
            v-model="query"
            class="search"
            placeholder="Поиск"
            :clearable="true"
            icon="search"
            @enter="applySearch"
          />
        </div>

        <div class="olap-reports__list">
          <div v-if="isEmpty" class="empty-message">
            <div v-if="query">Ничего не найдено. Попробуйте изменить параметры поиска</div>
            <div v-else>Отчетов пока нет</div>
          </div>

          <draggable-list
            v-model="list"
            @moveGroups="resortCategories"
            @addItemToGroup="onAddReportToCategory"
            @moveItemsInGroup="resortCategory"
            @removeItemFromGroup="onRemoveReportFromCategory"
          >
            <template #group="slotProps">
              <report-category
                :category="slotProps.group.data"
                :open="slotProps.group.open"
                @toggle="toggleCategory"
                @edit="editCategory"
                @remove="removeCategory"
              />
            </template>
            <template #item="slotProps">
              <report-item
                :report="slotProps.item"
                :code="code"
                @config="toReportConfig"
                @showDescription="showReportDescription"
                @edit="editReport"
                @remove="removeReport"
              />
            </template>
          </draggable-list>
        </div>
      </div>
    </div>

    <top-button root=".layout"></top-button>
  </div>
</template>

<script>
import TopButton from "@/components/top-button";
import CButton from "@/components/controls2/Button";
import CInput from "@/components/controls2/Input";
import Loader from "@/components/loader";
import MConfirm from "@/components/modals/confirm-modal";
import MDescription from "@/components/modals/description-modal";

import DraggableList from "./components/list";
import ReportCategory from "./components/report-category";
import ReportItem from "./components/report-item";
import MCategory from "./components/category-modal";
import MReport from "./components/report-modal";

import { CategoryModel } from "./models/category-model";
import { OlapReport } from "./models/olap-report";
import { CALCULATION_REPORT_CODE, REPORT_CODES, REPORT_TITLES } from "./utils/codes";
import {
  CHANGE_REPORT_CATEGORY_ACTION,
  GET_CATEGORIES_ACTION,
  GET_REPORTS_IN_CATEGORY_ACTION,
  REMOVE_CATEGORY_ACTION,
  REMOVE_REPORT_ACTION,
  RESORT_CATEGORIES_ACTION,
  RESORT_REPORTS_ACTION,
} from "./utils/actions";

export default {
  components: {
    CButton,
    CInput,
    Loader,
    DraggableList,
    ReportCategory,
    ReportItem,
    TopButton,
    MCategory,
    MReport,
    MConfirm,
    MDescription,
  },
  data() {
    return {
      bookmark: false,

      loading: false,
      list: [],
      query: "",

      code: CALCULATION_REPORT_CODE,
    };
  },

  computed: {
    normalizedQuery() {
      return this.query.trim().toLowerCase();
    },
    isEmpty() {
      return this.list.length === 1 && this.list[0].items.length === 0;
    },
    title() {
      return REPORT_TITLES[this.code];
    },
  },

  watch: {
    "$route.query.code": function (code) {
      if (REPORT_CODES.includes(code)) this.code = code;
      else this.code = CALCULATION_REPORT_CODE;
      document.title = this.title;
      this.updateList();
    },
  },

  created() {
    const { code } = this.$route.query;
    if (REPORT_CODES.includes(code)) this.code = code;
    document.title = this.title;
    this.updateList();
  },

  methods: {
    // GET DATA
    getCategories(params) {
      return this.$store
        .dispatch(GET_CATEGORIES_ACTION, params)
        .then((data) => data.map((category) => CategoryModel(category)));
    },
    getCategory(categoryId) {
      if (categoryId) return this.list.find((category) => category.id === categoryId);
      return this.list.find((category) => !category.id);
    },
    getReportsInCategory(params) {
      return this.$store
        .dispatch(GET_REPORTS_IN_CATEGORY_ACTION, params)
        .then((data) => data.map((report) => OlapReport(report)));
    },
    setReportsInCategory(reports, categoryId) {
      const reportsList = reports.map((report) => OlapReport(report));
      reportsList.sort((a, b) => a.sort - b.sort);

      const ids = [];

      reportsList.forEach((report) => {
        this.reports[report.id] = report;
        ids.push(report);
      });

      if (categoryId) this.reportsByCategories[categoryId] = ids;
      else this.reportsWithoutCategory = ids;
    },
    getParams() {
      return {
        search: this.normalizedQuery,
        code: this.code,
      };
    },
    applySearch() {
      this.updateList();
    },

    updateList() {
      this.loading = true;
      const params = this.getParams();

      Promise.all([this.getCategories(params), this.getReportsInCategory(params)]).then(([categories, reports]) => {
        const list = categories.map((category) => {
          const { id, reportsCount } = category;
          return {
            id,
            data: category,
            items: [],
            open: false,

            empty: reportsCount === 0,
          };
        });

        this.list = [
          ...list,
          {
            items: reports,
          },
        ];
        this.loading = false;
      });
    },

    // CATEGORIES

    createCategory() {
      this.$refs.categoryModal.open();
    },
    editCategory(category) {
      this.$refs.categoryModal.open(category);
    },
    onCategoryAdded(newCategory) {
      const model = CategoryModel(newCategory);
      this.list.unshift({
        id: model.id,
        data: model,
        items: [],
        open: false,
        empty: true,
      });
    },
    onCategoryUpdated(updatedCategory) {
      const category = this.list.find((c) => c.id === updatedCategory.id);
      if (category) {
        category.data.name = updatedCategory.name;
      }
    },
    toggleCategory({ id }) {
      const category = this.list.find((category) => id === category.id);
      if (category) {
        if (category.open) {
          category.open = false;
          category.items = [];
        } else {
          this.openCategory(category);
        }
      }
    },
    removeCategory(category) {
      this.$refs.confirm.open({
        title: "Удалить категорию",
        text: `Категория <b>${category.name}</b> будет удалена без возможности восстановления`,
        confirm: "Удалить",
        onConfirm: () => {
          this.$store.dispatch(REMOVE_CATEGORY_ACTION, category.id).then(() => {
            this.list = this.list.filter((cat) => cat.id !== category.id);
          });
        },
      });
    },
    openCategory(category) {
      category.open = true;
      this.updateCategoryItems(category);
    },
    updateCategoryItems(category) {
      category.loading = true;
      const params = {
        ...this.getParams(),
        categoryId: category.id,
      };

      this.getReportsInCategory(params).then((reports) => {
        this.list = this.list.map((cat) => {
          if (cat.id === category.id) {
            return {
              ...cat,
              items: reports,
              empty: !reports.length,
              loading: false,
            };
          }
          return cat;
        });
      });
    },

    // REPORTS

    createReport() {
      this.$refs.reportModal.open();
    },
    editReport(report) {
      this.$refs.reportModal.open(report.id);
    },
    removeReport(report) {
      this.$refs.confirm.open({
        title: "Удалить отчет",
        text: `Отчет <b>${report.name}</b> будет удален без возможности восстановления`,
        confirm: "Удалить",
        onConfirm: () => {
          this.$store.dispatch(REMOVE_REPORT_ACTION, { reportId: report.id, code: this.code }).then(() => {
            const category = this.getCategory(report.category);
            this.updateCategoryItems(category);
          });
        },
      });
    },
    toReportConfig(report) {
      this.$router.push({
        name: "olap-report",
        query: {
          code: this.code,
        },
        params: {
          id: report.id,
        },
      });
    },
    showReportDescription(report) {
      const { description, files } = report;
      this.$refs.description.open({
        description,
        files,
      });
    },
    onReportAdded(report) {
      this.$router.push({
        name: "olap-report",
        query: {
          code: this.code,
        },
        params: {
          id: report.id,
        },
      });
    },
    onReportUpdated() {
      this.updateList();
    },
    onReportRemoved(report) {
      const category = this.getCategory(report.category);
      this.updateCategoryItems(category);
    },

    // SORT
    resortCategories({ groups }) {
      const order = groups.map((group) => group.id).reverse();
      this.$store.dispatch(RESORT_CATEGORIES_ACTION, order);
    },
    resortCategory({ group }) {
      const order = group.items.map((item) => item.id).reverse();
      this.$store.dispatch(RESORT_REPORTS_ACTION, order);
    },
    onAddReportToCategory({ group, item }) {
      group.empty = false;
      group.open = true;
      if (group.data) group.data.reportsCount += 1;

      this.$store
        .dispatch(CHANGE_REPORT_CATEGORY_ACTION, {
          reportId: item.id,
          categoryId: group.id,
          code: this.code,
        })
        .then(() => {
          this.resortCategory({ group });
        });
    },
    onRemoveReportFromCategory({ group }) {
      if (!group.items.length) {
        group.empty = true;
        group.open = false;
        if (group.data) {
          group.data.reportsCount = 0;
        }
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.layout-header {
  height: auto;
  flex-direction: column;
  margin-bottom: 15px;

  .layout-title {
    margin-top: 0;
    margin-bottom: 20px;
  }

  &-actions {
    display: flex;
    flex-wrap: wrap;
  }
  &-action {
    margin-bottom: 10px;
  }

  @include bootstrapMd {
    flex-direction: row;
    margin-bottom: 25px;

    .layout-title {
      margin-bottom: 0;
    }

    &-actions {
      flex-wrap: wrap;
    }

    &-action {
      margin-bottom: 0;
    }
  }
}

.empty-message {
  font-size: 15px;
  color: $color-label;
  padding: 0 20px;

  @include bootstrapMd {
    padding: 0;
    margin-top: 15px;
  }
}

.olap-reports {
  &__search {
    margin-bottom: 20px;

    .search {
      width: 100%;

      @include bootstrapLg {
        width: 50%;
      }
    }
  }

  &__list {
    margin-left: -21px;
    margin-right: -21px;

    @media screen and (min-width: 700px) {
      margin-left: 0;
      margin-right: 0;
    }
  }
}
</style>
